import { useState } from 'react';
import { api } from '../lib/api';
import {
  type Language,
  honestLine,
  languageLabel,
  useLanguageCatalogue,
} from '../lib/languages';
import { useAction } from '../lib/useApi';
import { ErrorBox, Loading } from './ui';

/**
 * Picking a language in Ìran.
 *
 * Every language in the list works TODAY for the Story Vault, the Voice Tree and Grandparent
 * Calls — those have never needed lessons to exist. Lessons and games are written for Yorùbá and
 * nothing else yet, so every other language carries a badge that says exactly that, and a way to
 * tell us you want yours written next. No language is listed as "coming soon" to make the list
 * look longer.
 */

export function LanguageBadge({ language }: { language: Language }) {
  if (language.status === 'lessons_live') {
    return <span className="badge ok">Lessons, games and stories</span>;
  }
  return (
    <span className="badge warn" title="You can record and keep stories in this language today. Lessons and games are written for Yorùbá first.">
      Stories and voices now · lessons coming
    </span>
  );
}

/** One language chosen from the whole catalogue, grouped by region. */
export function LanguageSelect({
  id,
  value,
  onChange,
  label = 'Language',
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
  label?: string;
}) {
  const { catalogue, loading, error } = useLanguageCatalogue();
  if (loading) return <Loading label="Loading languages…" />;
  if (error) return <ErrorBox error={error} />;
  return (
    <select id={id} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {catalogue?.regions.map((region) => (
        <optgroup key={region.region} label={region.region}>
          {region.languages.map((l) => (
            <option key={l.code} value={l.code}>
              {languageLabel(l)}
              {l.status === 'lessons_live' ? ' — lessons live' : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * The honest note for a chosen language, plus — for anything without lessons — a way to say
 * "write ours next". Nothing here promises a date, because there isn't one.
 */
export function LanguageNote({ language, showRequest = true }: { language: Language | undefined; showRequest?: boolean }) {
  const act = useAction();
  const [asked, setAsked] = useState(false);
  if (!language) return null;

  if (language.status === 'lessons_live') {
    return (
      <p className="small muted" style={{ margin: 0 }}>
        {honestLine(language)}
      </p>
    );
  }

  return (
    <div className="alert info stack" style={{ gap: 8 }}>
      <p style={{ margin: 0 }}>{honestLine(language)}</p>
      {language.speakers_note && <p className="small muted" style={{ margin: 0 }}>{language.native_name} · {language.speakers_note}</p>}
      {showRequest &&
        (asked ? (
          <p className="small" style={{ margin: 0 }}>
            Thank you — we’ve noted that your family wants {language.name} lessons. Your {language.name} stories are safe in the vault meanwhile.
          </p>
        ) : (
          <div>
            <button
              type="button"
              className="btn small secondary"
              disabled={act.busy}
              onClick={async () => {
                const r = await act.run(() => api<{ requested: boolean }>(`/languages/${language.code}/request`, { method: 'POST', body: {} }));
                if (r) setAsked(true);
              }}
            >
              {act.busy ? 'Sending…' : `Tell us you want lessons in ${language.name}`}
            </button>
          </div>
        ))}
      <ErrorBox error={act.error} />
    </div>
  );
}

/**
 * Several languages at once, for "what is spoken in your home?". Region-grouped, searchable,
 * with one marked as the main language.
 */
export function LanguageChecklist({
  selected,
  primary,
  onToggle,
  onPrimary,
}: {
  selected: string[];
  primary: string | null;
  onToggle: (code: string) => void;
  onPrimary: (code: string) => void;
}) {
  const { catalogue, loading, error } = useLanguageCatalogue();
  const [query, setQuery] = useState('');
  if (loading) return <Loading label="Loading languages…" />;
  if (error) return <ErrorBox error={error} />;

  const q = query.trim().toLowerCase();
  const regions = (catalogue?.regions ?? [])
    .map((r) => ({ ...r, languages: r.languages.filter((l) => !q || l.name.toLowerCase().includes(q) || l.native_name.toLowerCase().includes(q) || l.code === q) }))
    .filter((r) => r.languages.length > 0);

  return (
    <div className="stack">
      <input
        type="search"
        aria-label="Search languages"
        placeholder="Search 90+ languages — Yorùbá, Igbo, Hausa, Twi, Kreyòl…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div style={{ maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>
        {regions.map((region) => (
          <fieldset key={region.region} style={{ border: 0, padding: 0, margin: '0 0 14px' }}>
            <legend className="label">{region.region}</legend>
            <div className="stack" style={{ gap: 6 }}>
              {region.languages.map((l) => {
                const on = selected.includes(l.code);
                return (
                  <div key={l.code} className="stack" style={{ gap: 2 }}>
                    <label className="check">
                      <input type="checkbox" checked={on} onChange={() => onToggle(l.code)} />
                      <span>
                        {l.name} <span className="yo" lang={l.code} dir={l.rtl ? 'rtl' : 'ltr'}>· {l.native_name}</span>{' '}
                        <LanguageBadge language={l} />
                      </span>
                    </label>
                    {on && (
                      <label className="check small" style={{ marginInlineStart: 28 }}>
                        <input type="radio" name="primary-language" checked={primary === l.code} onChange={() => onPrimary(l.code)} />
                        <span className="muted">Main language at home</span>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
        {regions.length === 0 && <p className="muted small">No language matches “{query}”. Tell us and we’ll add it.</p>}
      </div>
    </div>
  );
}
